import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { getYandexOAuthStatus, goalLabel, type OAuthStatusResponse } from '@/api/yandex'
import { bookmarkStep } from '@/api/onboarding'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { fireCelebration } from '@/lib/confetti'
import { track } from '@/lib/analytics'

export function YandexDonePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryChannelUUID = searchParams.get('channelUUID')
  const queryYandexLogin = searchParams.get('yandexLogin')

  const pinnedUUID = usePinnedChannelUUID()
  const channelUUID = queryChannelUUID || pinnedUUID
  const [oauthStatus, setOauthStatus] = useState<OAuthStatusResponse | null>(null)

  useEffect(() => {
    fireCelebration()
  }, [])

  useEffect(() => {
    if (!channelUUID) return
    getYandexOAuthStatus(channelUUID)
      .then((res) => setOauthStatus(res))
      .catch(() => {})
  }, [channelUUID])

  function handleNext() {
    const goals = oauthStatus?.goalsCreated.length ?? 0
    track('yandex_connected', {
      goalsCount: goals,
      message: `Я.Директ подключён · ${goals} ${pluralGoals(goals)}`,
    })
    bookmarkStep('requisites')
    navigate('/onboarding/requisites')
  }

  const goalsCount = oauthStatus?.goalsCreated.length ?? 0
  const goalNames = (oauthStatus?.goalsCreated ?? []).map(goalLabel)

  return (
    <OnboardingShell
      step="yandex-done"
      footer={
        <>
          <div className="foot-spacer" />
          <button className="foot-next" onClick={handleNext}>
            Дальше&nbsp;— реквизиты
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <div className="success-wrap">
        <div className="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </div>

        <p className="stage-eyebrow center">
          <span className="stage-eyebrow-pill success">Подключено</span>
        </p>
        <h1 className="stage-title center">
          Я.Директ <em>в&nbsp;деле</em>.
        </h1>
        <p className="stage-sub center">
          Заодно настроили счётчик Метрики и цели для аналитики&nbsp;— это нужно для подсчёта стоимости ПДП и оптимизации кампаний.
        </p>

        {oauthStatus && oauthStatus.hasYandexAuth && (
          <div className="success-source-card wide">
            <div className="ya-avatar">
              <svg viewBox="0 0 24 24" fill="white">
                <path d="M13.7 18h-1.9V9.5h-1.5l-.4 7.5H8.5l-.5-9h2.7l.5 8 1.4-8H14L13.7 18z" />
              </svg>
            </div>
            <div className="success-ch-meta">
              <div className="success-ch-name">
                Я.Директ{queryYandexLogin ? ` · ${queryYandexLogin}` : ''}
              </div>
              <div className="success-ch-subs">
                {oauthStatus.yaCounterID && (
                  <>
                    <span>
                      <b>Счётчик</b>&nbsp;{oauthStatus.yaCounterID}
                    </span>
                    <span className="dot-sep">·</span>
                  </>
                )}
                <span>
                  <b>{goalsCount} {pluralGoals(goalsCount)}</b>&nbsp;для конверсий
                </span>
              </div>
            </div>
            <div className="success-ch-status">подключён</div>
          </div>
        )}

        <div className="success-checklist">
          <CheckItem>
            Создан <b>счётчик Метрики</b> и привязан к вашему домену
          </CheckItem>
          <CheckItem>
            Настроены <b>{goalsCount > 0 ? `${goalsCount} ${pluralGoals(goalsCount)}` : 'цели'}</b>
            {goalNames.length > 0 && <>: {goalNames.join(', ').toLowerCase()}</>}
          </CheckItem>
          <CheckItem>
            Подключена <b>передача конверсий</b> в Я.Директ для оптимизации
          </CheckItem>
        </div>
      </div>

      <style>{`
        .success-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 700px;
          margin: 32px auto 0;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .success-icon {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: var(--tg-up, #00a860);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 28px;
          box-shadow: 0 12px 32px -8px rgba(0, 168, 96, 0.5);
          animation: success-pop 0.5s cubic-bezier(0.2,0.8,0.4,1) both;
          position: relative;
        }
        .success-icon::before, .success-icon::after {
          content: "";
          position: absolute;
          inset: -8px;
          border-radius: 50%;
          border: 1px solid rgba(0, 168, 96, 0.3);
          animation: success-ring 1.6s ease-out infinite;
        }
        .success-icon::after { animation-delay: 0.4s; }
        @keyframes success-pop {
          0% { transform: scale(0.6); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes success-ring {
          0% { inset: 0; opacity: 0.6; }
          100% { inset: -20px; opacity: 0; }
        }
        .success-icon svg { width: 48px; height: 48px; }
        .success-source-card {
          margin-top: 24px;
          width: 100%;
          max-width: 540px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          padding: 18px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          text-align: left;
          min-width: 0;
          box-sizing: border-box;
        }
        .ya-avatar {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: #FC3F1D;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .ya-avatar svg { width: 28px; height: 28px; }
        .success-ch-meta { flex: 1; min-width: 0; }
        .success-ch-name {
          font-weight: 700;
          font-size: 15px;
          color: var(--tg-ink, #111);
          margin-bottom: 4px;
        }
        .success-ch-subs {
          display: flex;
          gap: 8px;
          font-size: 12.5px;
          color: var(--tg-mute, #8f9eb0);
          font-variant-numeric: tabular-nums;
          flex-wrap: wrap;
        }
        .success-ch-subs b { color: var(--tg-ink, #111); font-weight: 700; }
        .success-ch-subs .dot-sep { color: var(--tg-line-3, #d0d0d8); }
        .success-ch-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 10px;
          background: rgba(0,168,96,0.1);
          color: var(--tg-up, #00a860);
          font-weight: 700;
          font-size: 11px;
          border-radius: 999px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .success-ch-status::before {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--tg-up, #00a860);
        }
        .success-checklist {
          margin-top: 24px;
          width: 100%;
          max-width: 540px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 0;
          box-sizing: border-box;
        }
        .success-check-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 16px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          text-align: left;
          line-height: 1.5;
          min-width: 0;
          box-sizing: border-box;
        }
        .success-check-item svg {
          width: 16px;
          height: 16px;
          color: var(--tg-up, #00a860);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .success-check-item b { color: var(--tg-ink, #111); font-weight: 700; }
        @media (max-width: 720px) {
          .success-wrap { margin-top: 20px; }
          .success-icon {
            width: 84px;
            height: 84px;
            margin-bottom: 22px;
          }
          .success-icon svg { width: 40px; height: 40px; }
          .success-source-card {
            padding: 16px;
            gap: 12px;
            align-items: flex-start;
            flex-wrap: wrap;
          }
          .success-ch-name,
          .success-check-item,
          .success-check-item span {
            overflow-wrap: anywhere;
          }
          .success-ch-meta {
            flex: 1 1 180px;
          }
          .success-ch-status {
            margin-left: 60px;
            max-width: calc(100% - 60px);
          }
          .success-check-item {
            padding: 12px 14px;
          }
        }
      `}</style>
    </OnboardingShell>
  )
}

function CheckItem({ children }: { children: React.ReactNode }) {
  return (
    <div className="success-check-item">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
      <div>{children}</div>
    </div>
  )
}

function pluralGoals(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'цель'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'цели'
  return 'целей'
}
